import React, {useCallback, useContext, useEffect, useRef} from 'react';
import Subshops from './Subshops.jsx';
import {
    applyFilterLandmark,
    deleteFilterParams,
    findEarlierDocumentEntryKey,
    getContentAjaxUrl,
    mergeSelectedFiltersWithParams,
    yieldToMain,
} from '../ArticleOverviewHelper';
import Landmark from '@core/scripts/components/landmark-adapter';

import {Constants} from '../view-constants';
import {setContentAjaxCall} from '../AjaxCallSlice.jsx';
import {useDispatch, useSelector} from 'react-redux';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {disableSticky, enableSticky, selectFilterStatus} from '../FilterSlice.jsx';
import {useBreakpointUp} from 'bonprix-design-system';
import FilterOrderingButton from '@article-overview/components/header/FilterOrderingButton.jsx';
import Filter from '@article-overview/components/filter/Filter.jsx';
import Device from '@core/scripts/components/device.js';
import {useQueryClient} from '@tanstack/react-query';
import {saveSizeFilterFromParams} from '../../utils/size-filter-persistence.js';

function FilterContainer({
    filterContent,
    landmarkContent,
    ordering,
    showOnlyTokens,
    filledArticleList,
    rootShopName,
    subshopsTopOffset,
}) {
    const {
        contentAjaxUrl,
        prettyUrl,
        currentSearchParams,
        rootCategoryPath,
        filterRootShopId,
        shopId,
        currentUrlPath,
        currentCategoryPath,
        shouldScrollUpAfterLoad,
        sizeFilterPersistenceEnabled
    } = useContext(articleOverviewStaticContext);

    const stickyFilterLine = useRef(null);
    const breakpointLUp = useBreakpointUp('l');
    const breakpointXLUp = useBreakpointUp('xl');

    const queryClient = useQueryClient()


    const isSticky = useSelector(selectFilterStatus).sticky;

    const dispatch = useDispatch();

    useEffect(() => {
        if (!stickyFilterLine.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    dispatch(disableSticky());
                } else {
                    dispatch(enableSticky());
                }
            },
            {
                root: null,
                threshold: 0,
                rootMargin: `-${breakpointXLUp ? subshopsTopOffset : 0}px 0px 0px 0px`,
            },
        );

        observer.observe(stickyFilterLine.current);

        return () => observer.disconnect();
    }, [subshopsTopOffset, breakpointXLUp]);

    const filterSubmit = useCallback(
        async (clickedFilters, clickedSortOption, retainShopIdFilterOnly = false, sendPageLandmark = false) => {
            await yieldToMain(() => {
                let mergedParams = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);

                applyFilterLandmark(
                    clickedFilters,
                    mergedParams,
                    shopId.current,
                    isSticky,
                    currentSearchParams.current,
                    filterRootShopId.current,
                );

                if (clickedSortOption) {
                    mergedParams.set(Constants.SEARCH_PARAMS_ORDERING, clickedSortOption.value);
                    Landmark.sendLandmark({
                        landmark: 'userinterface',
                        click: clickedSortOption.lm,
                        fixation: isSticky ? Constants.LANDMARK_STICKY : Constants.LANDMARK_NON_STICKY,
                    });
                }

                const path =
                    clickedFilters.get(Constants.CATEGORY_FILTER_IDENTIFIER)?.path || currentCategoryPath.current;

                if (clickedFilters.has(Constants.CATEGORY_FILTER_IDENTIFIER)) {
                    const targetPathname = currentUrlPath.current.replace(currentCategoryPath.current, path);
                    const earlierEntryKey = findEarlierDocumentEntryKey(targetPathname);
                    if (earlierEntryKey) {
                        // The page loaded there sends its own page view, so no page landmark is sent here.
                        window.navigation.traverseTo(earlierEntryKey);
                        return;
                    }
                }

                mergedParams.delete(Constants.SEARCH_PARAMS_PAGE);
                mergedParams.delete(Constants.SEARCH_PARAMS_ANCHOR);
                mergedParams.delete(Constants.SEARCH_PARAMS_SCROLL_Y);
                mergedParams.delete(Constants.SEARCH_PARAMS_SHOW_TEASER);

                if (retainShopIdFilterOnly) {
                    mergedParams = deleteFilterParams(mergedParams);
                }

                const formattedParams = `?${mergedParams.toString()}`;

                currentUrlPath.current = currentUrlPath.current.replace(currentCategoryPath.current, path);
                currentCategoryPath.current = path;
                currentSearchParams.current = mergedParams;
                shouldScrollUpAfterLoad.current = true;

                if (sizeFilterPersistenceEnabled) {
                    saveSizeFilterFromParams(mergedParams, shopId.current, filterRootShopId.current);
                }

                const contentAjaxCall = getContentAjaxUrl(path, formattedParams, contentAjaxUrl);

                //cleanup articles to avoid session storage overload
                queryClient.removeQueries({
                    queryKey: ['articles'],
                })
                dispatch(setContentAjaxCall([contentAjaxCall, retainShopIdFilterOnly, sendPageLandmark]));
            });
        },
        [
            prettyUrl.current,
            rootCategoryPath.current,
            shopId.current,
            filterContent.shopIdFilterGroup,
            currentSearchParams.current,
            currentCategoryPath.current,
            currentUrlPath.current,
            isSticky,
            sizeFilterPersistenceEnabled,
        ],
    );

    return (
        <>
            <div className="sticky-filter-line" ref={stickyFilterLine}></div>

            {filterContent.shopIdFilterGroup && filledArticleList && (
                <Subshops
                    identifier={filterContent.shopIdFilterGroup.identifier}
                    trackingId={filterContent.shopIdFilterGroup.filters[0]?.trackingId}
                    subshops={filterContent.shopIdFilterGroup.filters[0]?.filterOptions}
                    links={filterContent.shopIdFilterGroup.links}
                    filterSubmit={filterSubmit}
                    rootShopName={rootShopName}
                    subshopsTopOffset={subshopsTopOffset}
                    hasActiveFilters={filterContent.filterTokens?.length > 0}
                />
            )}

            <Filter filterContent={filterContent} filterSubmit={filterSubmit} ordering={ordering} />
        </>
    );
}

export default React.memo(FilterContainer);
